import styled, { keyframes } from "styled-components";
import { MARKETPLACE_COLORS, MARKETPLACE_FONTS } from "./MarketplaceConstants";

const gradient = MARKETPLACE_COLORS.BRAND_GRADIENT;

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const PageWrap = styled.div`
  min-height: 100vh;
  background: var(--mp-bodyBackground, #f7f8fc);
  font-family: ${MARKETPLACE_FONTS.SANS};
  color: var(--mp-textColor, #171717);
`;

export const Container = styled.div`
  max-width: 1680px;
  margin: 40px auto;
  padding: 0 40px 80px;

  @media (max-width: 768px) {
    margin: 20px auto;
    padding: 0 12px 60px;
  }
`;

export const StepContainer = styled.div`
  background: var(--mp-cardBackground, white);
  border-radius: 16px;
  padding: 40px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  text-align: center;
  max-width: 680px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
  }
`;

export const StepTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
  margin: 0 0 10px;
`;

export const StepDesc = styled.p`
  font-size: 14px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  margin: 0 0 30px;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const OptionCard = styled.div<{ $active: boolean }>`
  border: 2px solid ${p => p.$active ? "#c94daa" : "var(--mp-border, rgba(23, 23, 23, 0.08))"};
  background: var(--mp-cardBackground, white);
  border-radius: 12px;
  padding: 30px 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${p => p.$active && `
    background: rgba(201, 77, 170, 0.04);
    box-shadow: 0 8px 24px rgba(201, 77, 170, 0.08);

    [data-marketplace-theme="dark"] & {
      background: rgba(201, 77, 170, 0.15);
      box-shadow: 0 8px 24px rgba(201, 77, 170, 0.25);
    }
  `}

  &:hover {
    border-color: #c94daa;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
`;

export const IconCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--mp-iconGrad, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%));
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  svg {
    stroke: #ffffff !important;
  }
`;

export const CardName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 8px;
  color: var(--mp-headingColor, #171717);
`;

export const CardText = styled.p`
  font-size: 12px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.5));
  margin: 0;
  line-height: 1.5;
`;

export const NextBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 30px;
  background: var(--mp-heroBtnBg, ${gradient});
  color: var(--mp-heroBtnColor, white);
  border: none;
  border-radius: 9px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

export const LayoutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: start;

  @media (min-width: 992px) {
    grid-template-columns: 2fr 1.1fr;
  }
`;

export const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: sticky;
  top: 100px;
`;

export const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;

  @media (min-width: 600px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;

export const OnboardingLabel = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--mp-heroAccent, #c94daa);
  display: block;
  margin-bottom: 4px;
`;

export const PageStepTitle = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
  margin: 0 0 6px;
`;

export const PageStepDesc = styled.p`
  font-size: 14px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  margin: 0;
`;

export const StepIndicatorBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 10px;

  @media (min-width: 600px) {
    align-items: flex-end;
    margin-top: 0;
  }
`;

export const TopSkipBtn = styled.button`
  background: none;
  border: none;
  font-family: ${MARKETPLACE_FONTS.SANS};
  font-size: 13px;
  font-weight: 600;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: var(--mp-heroAccent, #c94daa);
    background: rgba(201, 77, 170, 0.08);
  }
`;

export const ProgressBars = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
`;

export const ProgressBar = styled.div<{ $active: boolean }>`
  width: 24px;
  height: 4px;
  border-radius: 2px;
  background: ${p => p.$active ? "var(--mp-heroBtnBg, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%))" : "var(--mp-border, rgba(23,23,23,0.1))"};
  transition: all 0.3s;
`;

export const StepText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const FormCard = styled.div`
  background: var(--mp-cardBackground, white);
  border-radius: 16px;
  padding: 32px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.07));
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const SectionLabel = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  font-family: ${MARKETPLACE_FONTS.SANS};
`;

export const Input = styled.input`
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.14));
  border-radius: 10px;
  font-size: 14px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  color: var(--mp-headingColor, #171717);
  background: var(--mp-bodyBackground, #fafafa);
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
    background: var(--mp-cardBackground, white);
  }
`;

export const Select = styled.select`
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.14));
  border-radius: 10px;
  font-size: 14px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  color: var(--mp-headingColor, #171717);
  background: var(--mp-bodyBackground, #fafafa);
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
  }
`;

export const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.14));
  border-radius: 10px;
  font-size: 14px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  color: var(--mp-headingColor, #171717);
  background: var(--mp-bodyBackground, #fafafa);
  resize: vertical;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
    background: var(--mp-cardBackground, white);
  }
`;

export const PremiumUploadBlock = styled.div`
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  background: var(--mp-bodyBackground, #f9fafc);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  min-height: 100px;
`;

export const PremiumUploadInner = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  cursor: pointer;
  text-align: left;
`;

export const CameraCircle = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px dashed var(--mp-border, rgba(201, 77, 170, 0.25));
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--mp-cardBackground, white);
  color: var(--mp-heroAccent, #c94daa);
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    border-color: var(--mp-heroAccent, #c94daa);
    transform: scale(1.05);
  }
`;

export const UploadInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong {
    font-size: 13px;
    font-weight: 700;
    color: var(--mp-headingColor, #171717);
  }

  span {
    font-size: 10px;
    color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.5));
    line-height: 1.3;
  }
`;

export const UploadActionLink = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: var(--mp-heroAccent, #c94daa) !important;
  text-decoration: underline;
  margin-top: 2px;
`;

export const PremiumImagePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
`;

export const PreviewImg = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--mp-heroAccent, #c94daa);
`;

export const PremiumRemoveImgBtn = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);

  &:hover {
    background: #dc2626;
  }
`;

export const PortfolioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PortfolioItemCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--mp-bodyBackground, #f7f8fc);
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.05));
`;

export const PortLabel = styled.div`
  font-size: 13px;
  color: var(--mp-headingColor, #171717);
`;

export const PortRemove = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AddPortRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const AddBtn = styled.button`
  height: 44px;
  padding: 0 24px;
  background: var(--mp-heroBtnBg, ${gradient});
  color: var(--mp-heroBtnColor, white);
  border: none;
  border-radius: 10px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  gap: 12px;
`;

export const ActionLeftGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

export const BackBtn = styled.button`
  height: 46px;
  padding: 0 28px;
  background: var(--mp-cardBackground, white);
  color: var(--mp-headingColor, #171717);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.15));
  border-radius: 10px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--mp-bodyBackground, #f9f9f9);
  }

  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

export const SkipBtn = styled.button`
  height: 46px;
  padding: 0 22px;
  background: var(--mp-cardBackground, white);
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.7));
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.15));
  border-radius: 10px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: var(--mp-bodyBackground, #f9f9f9);
    color: var(--mp-headingColor, #171717);
    border-color: var(--mp-heroAccent, #c94daa);
  }

  @media (max-width: 480px) {
    padding: 0 14px;
    font-size: 13px;
  }
`;

export const SubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 32px;
  background: var(--mp-heroBtnBg, ${gradient});
  color: var(--mp-heroBtnColor, white);
  border: none;
  border-radius: 10px;
  font-family: ${MARKETPLACE_FONTS.SANS};
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

export const SidebarCard = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  border-radius: 16px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
`;

export const BulbIconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 201, 81, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SidebarCardTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

export const SidebarCardText = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  line-height: 1.5;
`;

export const GradientCard = styled.div`
  background: var(--mp-heroBtnBg, ${gradient});
  border-radius: 16px;
  padding: 28px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 8px 30px rgba(201, 77, 170, 0.15);
`;

export const GradientSubTitle = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.85;
`;

export const GradientTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
`;

export const AvatarOverlayWrap = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

export const SmallAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid #ffffff;
`;

export const MoreBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #ffffff;
  margin-left: -10px;
  z-index: 0;
`;

export const PhotoModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); display: flex; align-items: center;
  justify-content: center; z-index: 1000; backdrop-filter: blur(4px);
`;

export const PhotoModalContent = styled.div`
  background: var(--mp-cardBackground, white);
  color: var(--mp-headingColor, #171717);
  padding: 24px; border-radius: 16px;
  width: 90%; max-width: 400px; box-shadow: 0 10px 25px rgba(0,0,0,0.25);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  display: flex; flex-direction: column; gap: 16px; text-align: center;
`;

export const ModalTitle = styled.h3`
  margin: 0; font-family: ${MARKETPLACE_FONTS.SANS}; font-size: 18px; font-weight: 700; color: var(--mp-headingColor, #171717);
`;

export const OptionButton = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 14px; border: 1px solid var(--mp-border, rgba(23,23,23,0.08)); border-radius: 10px;
  background: var(--mp-cardBackground, #f7f8fc); color: var(--mp-headingColor, #171717); font-family: ${MARKETPLACE_FONTS.SANS};
  font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  &:hover { opacity: 0.85; }
`;

export const CloseModalBtn = styled.button`
  border: none; background: transparent; color: var(--mp-heroSubColor, rgba(23,23,23,0.4));
  align-self: flex-end; cursor: pointer; margin-bottom: -10px;
  &:hover { color: var(--mp-headingColor, #171717); }
`;

export const CameraContainer = styled.div`
  width: 100%; border-radius: 12px; overflow: hidden; background: #000;
  position: relative; aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
`;

export const CameraVideo = styled.video`
  width: 100%; height: 100%; object-fit: cover;
`;

export const CameraControls = styled.div`
  display: flex; gap: 12px; justify-content: center; margin-top: 16px;
`;

export const CaptureBtn = styled.button`
  background: var(--mp-heroBtnBg, ${gradient}); color: var(--mp-heroBtnColor, white); border: none; padding: 10px 24px;
  border-radius: 8px; font-family: ${MARKETPLACE_FONTS.SANS}; font-weight: 700;
  font-size: 14px; cursor: pointer; box-shadow: 0 4px 10px rgba(0,0,0,0.2);
`;
